import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { CINEMA_PROVIDERS, CINEMA_PUBLIC_ENABLED } from '@/lib/cinema-providers';
import type { CinemaScreening } from '@/lib/content-types';
import { getTimeAgo } from '@/lib/timeUtils';
import { cn } from '@/lib/utils';

const JERUSALEM_TZ = 'Asia/Jerusalem';
const SCROLL_EDGE_SLOP = 2;
const HOME_CINEMA_VISIBLE_ROWS = 6;
const HOME_CINEMA_LIST_CLASS = 'h-[calc(6*3rem)] md:h-[calc(6*2.25rem)]';
const HOME_CINEMA_ROW_CLASS =
    'flex h-[3rem] min-w-0 items-start gap-2 overflow-hidden px-4 py-1 md:h-[2.25rem] md:items-center md:py-1.5 sm:px-6';
const HOME_CINEMA_TITLE_CLASS =
    'line-clamp-2 min-w-0 text-sm leading-snug text-white md:truncate md:leading-normal';

const ASHKELON_HINTS = ['אשקלון', 'עומר', 'אשדוד', 'באר שבע'];
const ALL_FILTER = '__all__';

function formatScreeningTime(iso: string) {
    return new Intl.DateTimeFormat('he-IL', {
        timeZone: 'Asia/Jerusalem',
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date(iso));
}

function formatScreeningDay(iso: string) {
    return new Intl.DateTimeFormat('he-IL', {
        timeZone: 'Asia/Jerusalem',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }).format(new Date(iso));
}

function isAshkelonArea(name: string) {
    const lower = name.toLowerCase();
    return ASHKELON_HINTS.some((hint) => lower.includes(hint));
}

/** Best-effort city/area label from chain-specific theater names. */
export function deriveCinemaCity(cinemaName: string) {
    const trimmed = cinemaName.trim();
    if (trimmed.startsWith('HOT CINEMA ')) return trimmed.slice('HOT CINEMA '.length);
    if (trimmed.startsWith('סינמה סיטי ')) return trimmed.slice('סינמה סיטי '.length);
    if (trimmed.startsWith('פלאנט ')) return trimmed.slice('פלאנט '.length);
    if (trimmed.startsWith('לב ')) return trimmed.slice('לב '.length);
    if (/^קולנוע \d+$/.test(trimmed)) return 'לא ידוע';
    return trimmed;
}

function theaterKey(row: Pick<CinemaScreening, 'provider' | 'cinemaName'>) {
    return `${row.provider}|${row.cinemaName}`;
}

type TheaterOption = {
    key: string;
    cinemaName: string;
    provider: string;
    providerLabel: string;
    city: string;
};

function buildTheaterOptions(screenings: CinemaScreening[]) {
    const map = new Map<string, TheaterOption>();
    for (const row of screenings) {
        const key = theaterKey(row);
        if (map.has(key)) continue;
        map.set(key, {
            key,
            cinemaName: row.cinemaName,
            provider: row.provider,
            providerLabel: row.providerLabel,
            city: deriveCinemaCity(row.cinemaName),
        });
    }
    return [...map.values()].sort((a, b) => {
        const aLocal = isAshkelonArea(a.cinemaName) ? 0 : 1;
        const bLocal = isAshkelonArea(b.cinemaName) ? 0 : 1;
        if (aLocal !== bLocal) return aLocal - bLocal;
        const cityCmp = a.city.localeCompare(b.city, 'he');
        if (cityCmp !== 0) return cityCmp;
        return a.cinemaName.localeCompare(b.cinemaName, 'he');
    });
}

function buildProviderOptions(screenings: CinemaScreening[]) {
    const byId = new Map<string, string>();
    for (const row of screenings) {
        if (!byId.has(row.provider)) {
            byId.set(row.provider, row.providerLabel);
        }
    }
    return CINEMA_PROVIDERS.filter((item) => byId.has(item.id)).map((item) => ({
        id: item.id,
        label: byId.get(item.id) ?? item.label,
    }));
}

const FILTER_SELECT_CLASS =
    'rounded-md border border-white/15 bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-[var(--brand)]';

type CinemaGroup = {
    cinemaName: string;
    providerLabel: string;
    movies: Map<string, CinemaScreening[]>;
};

function groupScreenings(screenings: CinemaScreening[]) {
    const byCinema = new Map<string, CinemaGroup>();

    for (const row of screenings) {
        const key = `${row.provider}|${row.cinemaName}`;
        let group = byCinema.get(key);
        if (!group) {
            group = {
                cinemaName: row.cinemaName,
                providerLabel: row.providerLabel,
                movies: new Map(),
            };
            byCinema.set(key, group);
        }
        const list = group.movies.get(row.movieTitle) ?? [];
        list.push(row);
        group.movies.set(row.movieTitle, list);
    }

    return [...byCinema.values()].sort((a, b) => {
        const aLocal = isAshkelonArea(a.cinemaName) ? 0 : 1;
        const bLocal = isAshkelonArea(b.cinemaName) ? 0 : 1;
        if (aLocal !== bLocal) return aLocal - bLocal;
        return a.cinemaName.localeCompare(b.cinemaName, 'he');
    });
}

function screeningDayKey(iso: string) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: JERUSALEM_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date(iso));
}

function todayDayKey() {
    return screeningDayKey(new Date().toISOString());
}

function formatHomeDayLabel(dayKey: string) {
    if (dayKey === todayDayKey()) return 'היום';

    const [year, month, day] = dayKey.split('-').map(Number);
    const date = new Date(Date.UTC(year!, month! - 1, day!, 12));
    return new Intl.DateTimeFormat('he-IL', {
        timeZone: JERUSALEM_TZ,
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
    }).format(date);
}

function formatHomeScreeningClock(iso: string) {
    return new Intl.DateTimeFormat('he-IL', {
        timeZone: JERUSALEM_TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date(iso));
}

type HomeCinemaGroup = {
    key: string;
    movieTitle: string;
    cinemaName: string;
    slots: CinemaScreening[];
};

function groupHomeDayScreenings(screenings: CinemaScreening[]): HomeCinemaGroup[] {
    const map = new Map<string, HomeCinemaGroup>();

    for (const row of screenings) {
        const key = `${row.movieTitle}|${row.cinemaName}`;
        let group = map.get(key);
        if (!group) {
            group = {
                key,
                movieTitle: row.movieTitle,
                cinemaName: row.cinemaName,
                slots: [],
            };
            map.set(key, group);
        }
        group.slots.push(row);
    }

    return [...map.values()]
        .map((group) => ({
            ...group,
            slots: group.slots.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
        }))
        .sort((a, b) => {
            const timeCmp = a.slots[0]!.startsAt.localeCompare(b.slots[0]!.startsAt);
            if (timeCmp !== 0) return timeCmp;
            return a.movieTitle.localeCompare(b.movieTitle, 'he');
        });
}

/** Compact cinema list for the homepage entertainment section. */
export function HomeCinemaStrip({
    screenings = [],
    className,
}: {
    screenings?: CinemaScreening[];
    className?: string;
}) {
    const listRef = useRef<HTMLDivElement>(null);
    const days = useMemo(() => {
        const keys = new Set<string>();
        for (const screening of screenings) {
            keys.add(screeningDayKey(screening.startsAt));
        }
        return [...keys].sort();
    }, [screenings]);

    const [dayIndex, setDayIndex] = useState(0);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    useEffect(() => {
        const today = todayDayKey();
        const nextIndex = days.findIndex((day) => day >= today);
        setDayIndex(nextIndex === -1 ? 0 : nextIndex);
    }, [days]);

    const selectedDay = days[dayIndex];
    const dayScreenings = useMemo(() => {
        if (!selectedDay) return [];
        return screenings.filter((screening) => screeningDayKey(screening.startsAt) === selectedDay);
    }, [screenings, selectedDay]);

    const groups = useMemo(
        () => groupHomeDayScreenings(dayScreenings),
        [dayScreenings],
    );
    const lastSyncedAt = useMemo(() => {
        if (screenings.length === 0) return null;
        return screenings.reduce(
            (latest, screening) =>
                screening.syncedAt > latest ? screening.syncedAt : latest,
            screenings[0]!.syncedAt,
        );
    }, [screenings]);
    const isEmptyDay = groups.length === 0;
    const hasExtraRows = !isEmptyDay && groups.length > HOME_CINEMA_VISIBLE_ROWS;
    const placeholderCount = isEmptyDay
        ? HOME_CINEMA_VISIBLE_ROWS - 1
        : hasExtraRows
          ? 0
          : Math.max(0, HOME_CINEMA_VISIBLE_ROWS - groups.length);
    const listResetKey = `${selectedDay ?? ''}:${groups.map((group) => group.key).join(',')}`;

    useEffect(() => {
        if (!hasExtraRows) {
            setCanScrollUp(false);
            setCanScrollDown(false);
            return;
        }

        const list = listRef.current;
        if (!list) return;

        list.scrollTop = 0;

        function syncScroll() {
            const { scrollTop, scrollHeight, clientHeight } = list;
            setCanScrollUp(scrollTop > SCROLL_EDGE_SLOP);
            setCanScrollDown(
                scrollTop + clientHeight < scrollHeight - SCROLL_EDGE_SLOP,
            );
        }

        syncScroll();
        list.addEventListener('scroll', syncScroll, { passive: true });
        const ro = new ResizeObserver(syncScroll);
        ro.observe(list);
        if (list.firstElementChild) ro.observe(list.firstElementChild);

        return () => {
            list.removeEventListener('scroll', syncScroll);
            ro.disconnect();
        };
    }, [listResetKey, hasExtraRows]);

    if (screenings.length === 0 || days.length === 0) return null;

    const canPrevDay = dayIndex > 0;
    const canNextDay = dayIndex < days.length - 1;

    return (
        <section
            className={cn(className)}
            aria-label="הקרנות קולנוע באשקלון"
        >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-white/10 px-4 py-2 sm:px-6">
                <p className="shrink-0 text-sm text-white/55">קולנוע</p>
                {lastSyncedAt ? (
                    <time
                        dateTime={lastSyncedAt}
                        className="text-xs text-white/45"
                    >
                        עודכן {getTimeAgo(lastSyncedAt)}
                    </time>
                ) : null}
                <div className="ms-auto flex min-w-0 select-none items-center gap-0.5">
                    <button
                        type="button"
                        className="inline-flex size-7 shrink-0 items-center justify-center text-white/55 transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-25"
                        disabled={!canPrevDay}
                        aria-label="יום קודם"
                        onClick={() => setDayIndex((index) => Math.max(0, index - 1))}
                    >
                        <ChevronRight className="size-3.5" aria-hidden />
                    </button>
                    <span className="min-w-[5.5rem] truncate text-center text-sm text-white">
                        {selectedDay ? formatHomeDayLabel(selectedDay) : ''}
                    </span>
                    <button
                        type="button"
                        className="inline-flex size-7 shrink-0 items-center justify-center text-white/55 transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-25"
                        disabled={!canNextDay}
                        aria-label="יום הבא"
                        onClick={() =>
                            setDayIndex((index) => Math.min(days.length - 1, index + 1))
                        }
                    >
                        <ChevronLeft className="size-3.5" aria-hidden />
                    </button>
                </div>
                {CINEMA_PUBLIC_ENABLED ? (
                    <Link
                        to="/cinema"
                        className="shrink-0 ps-2 text-xs text-white/55 transition-colors hover:text-white sm:text-sm"
                    >
                        הכל ←
                    </Link>
                ) : null}
            </div>

            <div className="relative">
                <div
                    ref={listRef}
                    className={cn(
                        'home-v-scroll-track home-cinema-scroll-track overflow-x-hidden',
                        HOME_CINEMA_LIST_CLASS,
                        hasExtraRows
                            ? 'overflow-y-auto overscroll-y-contain'
                            : 'overflow-hidden touch-none',
                    )}
                    aria-live="polite"
                >
                    <ul className="divide-y divide-white/10">
                        {isEmptyDay ? (
                            <li className={cn(HOME_CINEMA_ROW_CLASS, 'text-sm text-white/55')}>
                                אין הקרנות ביום זה.
                            </li>
                        ) : (
                            groups.map((group) => (
                                <li key={group.key} className={HOME_CINEMA_ROW_CLASS}>
                                    <div className="min-w-0 flex-1 overflow-hidden">
                                        <p
                                            className={HOME_CINEMA_TITLE_CLASS}
                                            title={`${group.movieTitle} · ${group.cinemaName}`}
                                        >
                                            {group.movieTitle}
                                            <span className="text-white/55">
                                                {' '}
                                                · {group.cinemaName}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 flex-nowrap justify-end gap-1 self-center">
                                        {group.slots.map((slot) => {
                                            const time = formatHomeScreeningClock(slot.startsAt);
                                            if (slot.bookingUrl) {
                                                return (
                                                    <a
                                                        key={slot.$id}
                                                        href={slot.bookingUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex min-w-[2.5rem] justify-center border border-white/15 px-1 py-0.5 text-xs tabular-nums text-white transition-colors hover:border-[var(--brand)] hover:text-white/90 sm:min-w-[2.75rem] sm:px-1.5"
                                                    >
                                                        {time}
                                                    </a>
                                                );
                                            }
                                            return (
                                                <span
                                                    key={slot.$id}
                                                    className="inline-flex min-w-[2.5rem] justify-center border border-white/10 px-1 py-0.5 text-xs tabular-nums text-white/55 sm:min-w-[2.75rem] sm:px-1.5"
                                                >
                                                    {time}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </li>
                            ))
                        )}
                        {placeholderCount > 0
                            ? Array.from({ length: placeholderCount }, (_, index) => (
                                  <li
                                      key={`placeholder-${selectedDay}-${index}`}
                                      className={HOME_CINEMA_ROW_CLASS}
                                      aria-hidden
                                  />
                              ))
                            : null}
                    </ul>
                </div>
                {hasExtraRows && canScrollUp ? (
                    <div className="home-cinema-scroll-fade-top" aria-hidden />
                ) : null}
                {hasExtraRows && canScrollDown ? (
                    <div className="home-cinema-scroll-fade-bottom" aria-hidden />
                ) : null}
            </div>
        </section>
    );
}

export function CinemaPageView({ screenings }: { screenings: CinemaScreening[] }) {
    const theaters = useMemo(() => buildTheaterOptions(screenings), [screenings]);
    const providers = useMemo(() => buildProviderOptions(screenings), [screenings]);
    const [providerFilter, setProviderFilter] = useState(ALL_FILTER);
    const [theaterFilter, setTheaterFilter] = useState(ALL_FILTER);

    const theatersForProvider = useMemo(() => {
        if (providerFilter === ALL_FILTER) return theaters;
        return theaters.filter((t) => t.provider === providerFilter);
    }, [providerFilter, theaters]);

    useEffect(() => {
        if (theaterFilter === ALL_FILTER) return;
        const selected = theaters.find((t) => t.key === theaterFilter);
        if (!selected) {
            setTheaterFilter(ALL_FILTER);
            return;
        }
        if (providerFilter !== ALL_FILTER && selected.provider !== providerFilter) {
            setTheaterFilter(ALL_FILTER);
        }
    }, [providerFilter, theaterFilter, theaters]);

    const filteredScreenings = useMemo(() => {
        let rows = screenings;
        if (providerFilter !== ALL_FILTER) {
            rows = rows.filter((row) => row.provider === providerFilter);
        }
        if (theaterFilter !== ALL_FILTER) {
            rows = rows.filter((row) => theaterKey(row) === theaterFilter);
        }
        return rows;
    }, [providerFilter, screenings, theaterFilter]);

    const groups = useMemo(() => groupScreenings(filteredScreenings), [filteredScreenings]);

    return (
        <>
            <section className="relative px-6 py-12 md:py-16">
                <p className="text-sm text-white/55">קולנוע</p>
                <h1 className="mt-3 text-4xl font-normal tracking-[-0.04em] md:text-5xl">
                    מה רץ באזור אשקלון
                </h1>
                <p className="mt-4 max-w-2xl text-base text-white/55">
                    זמני הקרנה מסונכרנים מרשתות הקולנוע המובילות. לחצו על שעה כדי לרכוש כרטיס
                    ישירות מהקולנוע.
                </p>
                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
                    <label className="flex min-w-[12rem] flex-col gap-1.5 text-sm text-white/70">
                        <span>רשת קולנוע</span>
                        <select
                            value={providerFilter}
                            onChange={(event) => {
                                setProviderFilter(event.target.value);
                                setTheaterFilter(ALL_FILTER);
                            }}
                            className={FILTER_SELECT_CLASS}
                        >
                            <option value={ALL_FILTER}>כל הרשתות</option>
                            {providers.map((provider) => (
                                <option key={provider.id} value={provider.id}>
                                    {provider.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="flex min-w-[14rem] flex-1 flex-col gap-1.5 text-sm text-white/70">
                        <span>בית קולנוע</span>
                        <select
                            value={theaterFilter}
                            onChange={(event) => setTheaterFilter(event.target.value)}
                            className={FILTER_SELECT_CLASS}
                            disabled={theatersForProvider.length === 0}
                        >
                            <option value={ALL_FILTER}>כל בתי הקולנוע</option>
                            {theatersForProvider.map((theater) => (
                                <option key={theater.key} value={theater.key}>
                                    {theater.cinemaName}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </section>

            {groups.length === 0 ? (
                <section className="border-t border-white/10 px-6 py-10">
                    <p className="text-base text-white/55">
                        אין כרגע הקרנות מעודכנות. נסו שוב מאוחר יותר.
                    </p>
                </section>
            ) : (
                groups.map((group) => (
                    <section key={`${group.providerLabel}-${group.cinemaName}`} className="border-t border-white/10">
                        <div className="px-6 py-4">
                            <p className="text-sm text-white/55">{group.providerLabel}</p>
                            <h2 className="mt-1 text-2xl font-normal tracking-[-0.03em]">{group.cinemaName}</h2>
                        </div>
                        <div className="divide-y divide-white/10 border-t border-white/10">
                            {[...group.movies.entries()].map(([movieTitle, slots]) => (
                                <article key={movieTitle} className="px-6 py-5">
                                    <h3 className="title-single-line text-lg text-white">{movieTitle}</h3>
                                    <ul className="mt-3 flex flex-wrap gap-2">
                                        {slots
                                            .slice()
                                            .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
                                            .map((slot) => (
                                                <li key={slot.$id}>
                                                    {slot.bookingUrl ? (
                                                        <a
                                                            href={slot.bookingUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex min-w-[5.5rem] flex-col rounded-md border border-white/15 px-3 py-2 text-center transition-colors hover:border-[var(--brand)] hover:bg-white/[0.04]"
                                                            title={`${formatScreeningDay(slot.startsAt)}${slot.auditorium ? ` - ${slot.auditorium}` : ''}`}
                                                        >
                                                            <span className="text-sm text-white">{formatScreeningTime(slot.startsAt)}</span>
                                                            {slot.auditorium ? (
                                                                <span className="mt-1 text-xs text-white/45">{slot.auditorium}</span>
                                                            ) : null}
                                                        </a>
                                                    ) : (
                                                        <span className="inline-flex min-w-[5.5rem] flex-col rounded-md border border-white/10 px-3 py-2 text-center text-sm text-white/70">
                                                            {formatScreeningTime(slot.startsAt)}
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    </section>
                ))
            )}
        </>
    );
}
